/*
 * <cart-looks>: COMPLETE YOUR LOOK in the cart (snippets/cart-looks.liquid). For each cart piece it
 * fetches that piece's partners as finished tiles (sections/look-candidates.liquid, through the
 * recommendations endpoint), ranks them (assets/look-rank.js) and shows up to eight in a swipe row.
 * The + opens the piece's sizes in place; a size adds it to the cart, records it in the cart
 * attribute _umi_look_adds (what the block sold), and the cart redraws.
 *
 * A piece a Vitals set row offers on the same surface is left out too (assets/set-offer.js): the
 * set carries the discount, the look does not.
 *
 * The drawer replaces its list on every change, so this element is new each time: the partners
 * and the last painted row live at module level, keyed by the cart's pieces, so a redraw paints at
 * once and re-ranks. In the drawer it loads when the drawer opens; on the cart page at once.
 */
if (!customElements.get("cart-looks")) {
  const responses = new Map();
  let lastPaint = { key: "", html: "" };
  // An add redraws the cart, and with it this element and the live region: the next element says
  // what was added and puts focus on the tile that took the added one's place.
  let pendingAdd = null;

  const fetchPartners = (url, locale, productId) => {
    const key = `${locale}|${productId}`;
    if (!responses.has(key)) {
      const request = fetch(`${url}&product_id=${productId}&section_id=look-candidates`)
        .then((response) => {
          if (!response.ok) throw new Error(`Complete your look: ${response.status}`);
          return response.text();
        })
        .then((text) => new DOMParser().parseFromString(text, "text/html").querySelector("[data-look-candidates]"));
      request.catch(() => responses.delete(key));
      responses.set(key, request);
    }
    return responses.get(key);
  };

  const tileData = (element) => {
    let sizes = [];
    try {
      sizes = JSON.parse(element.dataset.sizes || "[]");
    } catch (error) {
      sizes = [];
    }
    return {
      element,
      productId: element.dataset.productId,
      position: Number(element.dataset.position) || 0,
      role: element.dataset.role,
      best: element.dataset.best === "true",
      new: element.dataset.new === "true",
      bought: Number(element.dataset.bought) || 0,
      price: Number(element.dataset.price) || 0,
      colour: element.dataset.colour || "",
      sizes,
    };
  };

  class CartLooks extends HTMLElement {
    connectedCallback() {
      if (this.dataset.fitting === "true") return;
      try {
        this.anchors = JSON.parse(this.querySelector("[data-look-anchors]")?.textContent || "[]");
      } catch (error) {
        this.anchors = [];
      }
      if (!this.anchors.length) return;
      this.row = this.querySelector("[data-look-row]");
      this.key = JSON.stringify([
        this.dataset.locale,
        this.dataset.cartProducts,
        this.dataset.remainder,
        this.anchors,
      ]);
      if (lastPaint.key === this.key && lastPaint.html) this.paint(lastPaint.html);
      this.row.addEventListener("click", (event) => this.onClick(event));
      // A Vitals set row offers a partner with its discount; that partner leaves the looks.
      this.onSetOffer = () => {
        if (this.run) this.load();
      };
      document.addEventListener("set-offer:change", this.onSetOffer);

      const drawer = this.closest("cart-drawer");
      if (!drawer || drawer.classList.contains("active")) {
        this.load();
      } else {
        this.opened = new MutationObserver(() => {
          if (!drawer.classList.contains("active")) return;
          this.opened.disconnect();
          this.load();
        });
        this.opened.observe(drawer, { attributes: true, attributeFilter: ["class"] });
      }
    }

    disconnectedCallback() {
      this.opened?.disconnect();
      document.removeEventListener("set-offer:change", this.onSetOffer);
      this.run = null;
    }

    async load() {
      const run = (this.run = {});
      const { url, locale } = this.dataset;
      const fetched = await Promise.all(
        this.anchors.map((anchor) =>
          fetchPartners(url, locale, anchor.product_id)
            .then((container) => ({ anchor, container }))
            .catch(() => ({ anchor, container: null })),
        ),
      );
      if (this.run !== run || !this.isConnected) return;
      const lists = fetched
        .filter(({ container }) => container)
        .map(({ anchor, container }) => ({
          anchor,
          tiles: Array.from(container.querySelectorAll("[data-look-tile]"), tileData),
        }));
      const pairs = fetched.map(({ container }) => container?.dataset.pairs || "").join("");
      // Left out: the pieces in the cart, and those a Vitals set row on this surface offers.
      const scope = this.closest("cart-drawer") || this.closest("cart-items") || document;
      const inSet = globalThis.UMISetOffer?.offeredIds(scope) || [];
      const offers = globalThis.UMILookRank.rank(lists, {
        anchors: this.anchors,
        productIds: new Set([...(this.dataset.cartProducts || "").split(",").filter(Boolean), ...inSet]),
        remembered: CartLooks.rememberedSize(),
        remainder: Number(this.dataset.remainder) || null,
        pairs,
      });
      const html = offers
        .map(({ tile }) => {
          const copy = tile.element.cloneNode(true);
          copy.setAttribute("role", "listitem");
          return copy.outerHTML;
        })
        .join("");
      lastPaint = { key: this.key, html };
      this.paint(html);
    }

    paint(html) {
      this.row.innerHTML = html;
      this.hidden = !html;
      if (!pendingAdd || !this.isConnected) return;
      const { message, index, surface } = pendingAdd;
      if (surface !== this.dataset.surface) return;
      pendingAdd = null;
      const live = document.getElementById(surface === "drawer" ? "CartDrawer-LiveRegionText" : "cart-live-region-text");
      if (live) live.textContent = message;
      const pluses = this.row.querySelectorAll("[data-look-add]");
      const next = pluses[Math.min(index, pluses.length - 1)];
      (next || this.querySelector(".cart-looks__heading"))?.focus?.();
    }

    onClick(event) {
      const size = event.target.closest("[data-look-size]");
      if (size) {
        this.add(size.dataset.variantId, size.closest("[data-look-tile]"));
        return;
      }
      const plus = event.target.closest("[data-look-add]");
      if (!plus) return;
      const tile = plus.closest("[data-look-tile]");
      const sizes = tileData(tile).sizes;
      const inStock = sizes.filter((entry) => entry.available);
      // A piece in one size adds on the first tap.
      if (sizes.length <= 1 && inStock.length === 1) {
        this.add(inStock[0].id, tile);
        return;
      }
      const list = tile.querySelector("[data-look-sizes]");
      const open = list.hidden;
      if (open && !list.childElementCount) {
        list.innerHTML = sizes
          .map((entry) =>
            entry.available
              ? `<li><button type="button" class="cart-look__size" data-look-size data-variant-id="${entry.id}">${CartLooks.escape(entry.value)}</button></li>`
              : `<li><span class="cart-look__size cart-look__size--struck">${CartLooks.escape(entry.value)} <span class="visually-hidden">${CartLooks.escape(this.dataset.soldOutLabel || "")}</span></span></li>`,
          )
          .join("");
      }
      list.hidden = !open;
      plus.setAttribute("aria-expanded", String(open));
    }

    async add(variantId, tile) {
      if (this.busy || !variantId) return;
      this.busy = true;
      globalThis.UMITbyb?.cartMutationStart();
      try {
        const adds = [this.dataset.adds, variantId].filter(Boolean).join(",");
        const response = await fetch(`${window.routes.cart_update_url}.js`, {
          ...fetchConfig(),
          body: JSON.stringify({
            updates: { [variantId]: 1 },
            attributes: { _umi_look_adds: adds },
            // The header's bag count: the cart redraws below do not touch it.
            sections: ["cart-icon-bubble"],
            sections_url: window.location.pathname,
          }),
        });
        const cart = await response.json();
        if (!response.ok || cart.status) {
          throw Object.assign(new Error(cart.description || ""), { shopify: true });
        }
        const bubble = document.getElementById("cart-icon-bubble");
        const bubbleHtml = cart.sections?.["cart-icon-bubble"];
        if (bubble && bubbleHtml) {
          const fresh = new DOMParser().parseFromString(bubbleHtml, "text/html").querySelector(".shopify-section");
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }
        const title = tile?.querySelector(".cart-look__name")?.textContent.trim() || "";
        const tiles = Array.from(this.row.querySelectorAll("[data-look-tile]"));
        pendingAdd = {
          message: (this.dataset.addedLabel || "").replace("[title]", title),
          index: Math.max(tiles.indexOf(tile), 0),
          surface: this.dataset.surface,
        };
        publish(PUB_SUB_EVENTS.cartUpdate, { source: "cart-looks", cartData: cart, variantId: Number(variantId) });
      } catch (error) {
        const errors = document.getElementById(this.dataset.surface === "drawer" ? "CartDrawer-CartErrors" : "cart-errors");
        // Shopify's own reason (translated) or the store's error, never a raw script message.
        if (errors) errors.textContent = (error.shopify && error.message) || window.cartStrings?.error || "";
      } finally {
        globalThis.UMITbyb?.cartMutationEnd();
        this.busy = false;
      }
    }

    static rememberedSize() {
      try {
        return window.localStorage.getItem("umi.size") || "";
      } catch (error) {
        return "";
      }
    }

    static escape(value) {
      return `${value}`.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
    }
  }

  customElements.define("cart-looks", CartLooks);
}
